/**
 * Security Middleware for E-commerce Application
 * Handles unsafe requests, rate limiting, and loop prevention
 */

interface RequestMetrics {
  count: number;
  lastRequest: number;
  blockedUntil?: number;
}

interface SecurityConfig {
  rateLimit: {
    windowMs: number; // Time window in milliseconds
    maxRequests: number; // Max requests per window
    blockDurationMs: number; // How long to block after limit exceeded
  };
  loopDetection: {
    maxSameRequests: number; // Max identical requests in window
    timeWindowMs: number; // Time window for loop detection
  };
  sanitization: {
    allowedMethods: string[];
    blockedPatterns: RegExp[];
    maxPayloadSize: number; // Max payload size in bytes
  };
}

class SecurityMiddleware {
  private requestMetrics = new Map<string, RequestMetrics>();
  private requestHistory = new Map<string, number[]>();
  private blockedIPs = new Set<string>();
  
  private config: SecurityConfig = {
    rateLimit: {
      windowMs: 60000, // 1 minute
      maxRequests: 100, // 100 requests per minute
      blockDurationMs: 300000, // Block for 5 minutes
    },
    loopDetection: {
      maxSameRequests: 10, // Max 10 identical requests
      timeWindowMs: 10000, // Within 10 seconds
    },
    sanitization: {
      allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
      blockedPatterns: [
        /(<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>)/gi, // Script tags
        /(javascript:|vbscript:|onload=|onerror=)/gi, // JS execution
        /(union\s+select|drop\s+table|insert\s+into)/gi, // SQL injection
        /(\.\.\/|\.\.\\)/g, // Path traversal
        /(<iframe|<object|<embed)/gi, // Embedded content
      ],
      maxPayloadSize: 1024 * 1024 * 5, // 5MB max payload
    },
  };

  /**
   * Get client identifier (IP or user ID)
   */
  private getClientId(request: any): string {
    // Try to get user ID from auth context
    const userId = request.userId || request.user?.id;
    if (userId) return `user:${userId}`;

    // Fallback to IP address
    const ip = request.ip || 
               request.connection?.remoteAddress || 
               request.socket?.remoteAddress ||
               '127.0.0.1';
    return `ip:${ip}`;
  }

  /**
   * Generate request signature for loop detection
   */
  private getRequestSignature(request: any): string {
    const method = request.method || 'GET';
    const url = request.url || '/';
    const body = typeof request.body === 'object' ? JSON.stringify(request.body) : request.body || '';
    return `${method}:${url}:${body}`;
  }

  /**
   * Clean up old metrics to prevent memory leaks
   */
  private cleanupMetrics(): void {
    const now = Date.now();
    const cleanupThreshold = now - (this.config.rateLimit.windowMs * 2);

    for (const [key, metrics] of this.requestMetrics.entries()) {
      if (metrics.lastRequest < cleanupThreshold && (!metrics.blockedUntil || metrics.blockedUntil < now)) {
        this.requestMetrics.delete(key);
      }
    }

    for (const [key, timestamps] of this.requestHistory.entries()) {
      const filteredTimestamps = timestamps.filter(ts => ts > cleanupThreshold);
      if (filteredTimestamps.length === 0) {
        this.requestHistory.delete(key);
      } else {
        this.requestHistory.set(key, filteredTimestamps);
      }
    }
  }

  /**
   * Check if request is rate limited
   */
  private checkRateLimit(clientId: string): { allowed: boolean; retryAfter?: number } {
    const now = Date.now();
    const metrics = this.requestMetrics.get(clientId);

    if (!metrics) {
      this.requestMetrics.set(clientId, { count: 1, lastRequest: now });
      return { allowed: true };
    }

    // Check if currently blocked
    if (metrics.blockedUntil && metrics.blockedUntil > now) {
      return { 
        allowed: false, 
        retryAfter: Math.ceil((metrics.blockedUntil - now) / 1000) 
      };
    }

    // Reset window if needed
    if (now - metrics.lastRequest > this.config.rateLimit.windowMs) {
      metrics.count = 1;
      metrics.lastRequest = now;
      delete metrics.blockedUntil;
      return { allowed: true };
    }

    // Increment count
    metrics.count++;
    metrics.lastRequest = now;

    // Check if limit exceeded
    if (metrics.count > this.config.rateLimit.maxRequests) {
      metrics.blockedUntil = now + this.config.rateLimit.blockDurationMs;
      return { 
        allowed: false, 
        retryAfter: Math.ceil(this.config.rateLimit.blockDurationMs / 1000) 
      };
    }

    return { allowed: true };
  }

  /**
   * Detect potential infinite loops or spam
   */
  private detectLoops(clientId: string, signature: string): boolean {
    const now = Date.now();
    const key = `${clientId}:${signature}`;
    const history = this.requestHistory.get(key) || [];

    // Clean old entries
    const recentHistory = history.filter(
      timestamp => now - timestamp < this.config.loopDetection.timeWindowMs
    );

    // Add current request
    recentHistory.push(now);
    this.requestHistory.set(key, recentHistory);

    // Check if loop detected
    return recentHistory.length > this.config.loopDetection.maxSameRequests;
  }

  /**
   * Sanitize request data
   */
  private sanitizeRequest(request: any): { safe: boolean; issues: string[] } {
    const issues: string[] = [];

    // Check method
    if (!this.config.sanitization.allowedMethods.includes(request.method?.toUpperCase())) {
      issues.push(`Unsafe HTTP method: ${request.method}`);
    }

    // Check payload size
    const payloadSize = JSON.stringify(request.body || {}).length;
    if (payloadSize > this.config.sanitization.maxPayloadSize) {
      issues.push(`Payload too large: ${payloadSize} bytes`);
    }

    // Check for malicious patterns
    const checkString = JSON.stringify({
      url: request.url,
      body: request.body,
      query: request.query,
      headers: request.headers,
    });

    for (const pattern of this.config.sanitization.blockedPatterns) {
      if (pattern.test(checkString)) {
        issues.push(`Blocked pattern detected: ${pattern.source}`);
      }
    }

    return { safe: issues.length === 0, issues };
  }

  /**
   * Main middleware function
   */
  public async validateRequest(request: any): Promise<{
    allowed: boolean;
    status?: number;
    message?: string;
    retryAfter?: number;
  }> {
    try {
      // Cleanup old metrics periodically
      if (Math.random() < 0.01) { // 1% chance to cleanup
        this.cleanupMetrics();
      }

      const clientId = this.getClientId(request);
      const signature = this.getRequestSignature(request);

      // Check if IP is permanently blocked
      if (this.blockedIPs.has(clientId)) {
        return {
          allowed: false,
          status: 403,
          message: 'Client permanently blocked due to security violations',
        };
      }

      // Rate limiting check
      const rateLimitResult = this.checkRateLimit(clientId);
      if (!rateLimitResult.allowed) {
        return {
          allowed: false,
          status: 429,
          message: 'Rate limit exceeded. Please try again later.',
          retryAfter: rateLimitResult.retryAfter,
        };
      }

      // Loop detection
      if (this.detectLoops(clientId, signature)) {
        console.warn(`Loop detected for client ${clientId}:`, signature);
        return {
          allowed: false,
          status: 429,
          message: 'Too many identical requests. Please wait before retrying.',
        };
      }

      // Request sanitization
      const sanitizationResult = this.sanitizeRequest(request);
      if (!sanitizationResult.safe) {
        console.warn(`Unsafe request blocked for ${clientId}:`, sanitizationResult.issues);
        
        // Consider blocking repeat offenders
        const metrics = this.requestMetrics.get(clientId);
        if (metrics && metrics.count > 50) { // Many requests with security issues
          this.blockedIPs.add(clientId);
        }

        return {
          allowed: false,
          status: 400,
          message: 'Request blocked due to security policy violations',
        };
      }

      return { allowed: true };

    } catch (error) {
      console.error('Security middleware error:', error);
      // Fail secure - block the request if we can't validate it
      return {
        allowed: false,
        status: 500,
        message: 'Security validation failed',
      };
    }
  }

  /**
   * Configure security settings
   */
  public configure(newConfig: Partial<SecurityConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Manually block a client
   */
  public blockClient(clientId: string): void {
    this.blockedIPs.add(clientId);
  }

  /**
   * Unblock a client
   */
  public unblockClient(clientId: string): void {
    this.blockedIPs.delete(clientId);
    this.requestMetrics.delete(clientId);
    
    // Remove from request history
    for (const key of this.requestHistory.keys()) {
      if (key.startsWith(clientId)) {
        this.requestHistory.delete(key);
      }
    }
  }

  /**
   * Get current metrics for monitoring
   */
  public getMetrics(): {
    activeClients: number;
    blockedClients: number;
    totalRequests: number;
  } {
    let totalRequests = 0;
    for (const metrics of this.requestMetrics.values()) {
      totalRequests += metrics.count;
    }

    return {
      activeClients: this.requestMetrics.size,
      blockedClients: this.blockedIPs.size,
      totalRequests,
    };
  }
}

// Export singleton instance
export const securityMiddleware = new SecurityMiddleware();

// Export types for configuration
export type { SecurityConfig };
